// LessonContent.js
import React, {useEffect, useState} from "react";

import AudioPlayer from "./AudioPlayer";
import AudioRecorder from "./AudioRecorder";
import PitchPlot from "./PitchPlot";

const LessonContent = ({selectedLesson, onBack}) => {
  const [selectedLessonData, setSelectedLessonData] = useState(null);
  const [initialData, setInitialData] = useState({time : [], pitch : []});
  const [recordedData, setRecordedData] = useState({time : [], pitch : []});
  const [word, setWord] = useState({
    ch : "",
    pinyin : "",
    en : "",
    tone : "",
    path : "",
  });
  const [filename, setFilename] = useState("");
  const [index, setIndex] = useState(0);
  const [gender, setGender] = useState("male");
  const [selectedTones, setSelectedTones] = useState([]);

  const tones = [ "1", "2", "3", "4" ];
  const toneCombinations = [
    [ "1, 1", "1, 2", "1, 3", "1, 4" ],
    [ "2, 1", "2, 2", "2, 3", "2, 4" ],
    [ "3, 1", "3, 2", "3, 3", "3, 4" ],
    [ "4, 1", "4, 2", "4, 3", "4, 4" ],
  ];

  useEffect(() => {
    if (selectedLesson) {
      fetch(`/static/${selectedLesson.path}`)
          .then((response) => response.json())
          .then((data) => {
            if (data) {
              setSelectedLessonData(data);
            } else {
              console.error("could not fetch lesson data");
            }
          })
          .catch((error) =>
                     console.error("error fetching lesson data:", error));
    }
  }, [ selectedLesson ]);

  useEffect(() => {
    if (selectedLesson && selectedLessonData && selectedLessonData.length > 0) {
      setWord({
        ch : selectedLessonData[index].ch,
        pinyin : selectedLessonData[index].pinyin,
        en : selectedLessonData[index].en,
        tone : selectedLessonData[index].tone,
        path : selectedLessonData[index].path,
      });
    } else {
      console.error("could not set word");
    }
  }, [ selectedLessonData, index ]);

  useEffect(() => {
    if (word.ch) {
      fetch(`/static/${word.path}/${gender}/${word.ch}.json`)
          .then((response) => response.json())
          .then((data) => {
            if (data && data.pitch) {
              setInitialData({
                time : data.time,
                pitch : data.pitch,
              });
              setFilename(`${word.path}/${gender}/${word.ch}`);
            }
          })
          .catch((error) => console.error("Error fetching pitch contour data:",
                                          error));
    }
  }, [ word ]);

  const handleToneChange = (tone) => {
    setRecordedData({time : [], pitch : []});
    setSelectedTones((prevSelected) =>
                         prevSelected.includes(tone)
                             ? prevSelected.filter((t) => t !== tone)
                             : [...prevSelected, tone ]);
  };

  const renderToneCheckboxes = () => (
    <div>
      {tones.map((tone) => (
        <label key={tone}>
          <input
  type = "checkbox"
            value={tone}
            checked={selectedTones.includes(tone)}
            onChange={
    () => handleToneChange(tone)}
          />
          Tone {tone}
        </label>
      ))}
    </div>
  );

  const handleGenderChange = (event) => {
    setGender(event.target.value); // Update gender based on selected radio button
};

  const renderToneCombinationCheckboxes = () => (
    <div style={styles.toneCombinationContainer}>
      {toneCombinations.map((group, groupIndex) => (
        <div key={groupIndex} style={styles.toneGroup}>
          {group.map((combination) => (
            <label key={combination} style={styles.checkboxLabel}>
              <input
  type = "checkbox"
                value={combination}
                checked={selectedTones.includes(combination)}
                onChange={
    () => handleToneChange(combination)}
              />
              Tone {combination}
            </label>
          ))}
        </div>
      ))}
    </div>
  );

  const handleNext = () => {
    if (selectedLessonData) {
      const filteredData =
          selectedTones.length > 0
              ? selectedLessonData.filter((item) =>
                                              selectedTones.includes(item.tone))
              : selectedLessonData;

      if (index < filteredData.length - 1) {
        setIndex((prevIndex) => prevIndex + 1);
        setRecordedData({time : [], pitch : []});
      } else {
        setIndex(0);
      }
    }
  };

  useEffect(() => {
    if (selectedLesson && selectedLessonData && selectedLessonData.length > 0) {
      const filteredData =
          selectedTones.length > 0
              ? selectedLessonData.filter((item) =>
                                              selectedTones.includes(item.tone))
              : selectedLessonData;

      if (filteredData.length > 0) {
        setWord({
          ch : filteredData[index]?.ch,
          pinyin : filteredData[index]?.pinyin,
          en : filteredData[index]?.en,
          tone : filteredData[index]?.tone,
          path : filteredData[index]?.path,
        });
      } else {
        console.error("No words match the selected tones");
      }
    }
  }, [selectedLessonData, index, selectedTones]);

  const handleBack = () => {
    onBack();
    setRecordedData({time : [], pitch : []});
  };

  const normalizeTime = (initialTime, recordedTime) => {
    const initialStartTime = initialTime.length > 0 ? initialTime[0] : 0;
    const recordedStartTime = recordedTime.length > 0 ? recordedTime[0] : 0;

    const shiftTo = 0.0;

    const initialNormalized =
        initialTime.map((t) => t - initialStartTime + shiftTo * 1000);
    const recordedNormalized =
        recordedTime.map((t) => t - recordedStartTime + shiftTo * 1000);

    return {initialNormalized, recordedNormalized};
  };

  const handleRecordingComplete = async (data) => {
    if (data && data.pitch && data.time) {
      const normalized = normalizeTime(initialData.time, data.time);

      setRecordedData({
        pitch : data.pitch,
        time : normalized.recordedNormalized,
      });

      setInitialData((prevInitialData) => ({
                       pitch : prevInitialData.pitch,
                       time : normalized.initialNormalized,
                     }));
    }
  };

  return (
    <div style={styles.mainContent}>
      <h1>Pitch Contour Analyzer</h1>
      {/* Radio Buttons for Gender Selection */}
      <div style={styles.genderSelector}>
        <label>
          <input
            type="radio"
            value="male"
            checked={gender === "male"}
            onChange={handleGenderChange}
          />
          Male
        </label>
        <label>
          <input
            type="radio"
            value="female"
            checked={gender === "female"}
            onChange={handleGenderChange}
          />
          Female
        </label>
      </div>

      {initialData && (
        <div style={styles.plotContainer}>
          <PitchPlot
            initialTime={initialData.time}
            initialPitch={initialData.pitch}
            recordedTime={recordedData.time}
            recordedPitch={
    recordedData.pitch}
          />
        </div>
      )}

      <div style={styles.audioPlayer}></div>

      <p>
        <span style={styles.word}>{word.ch}</span>
      </p>
      <p>
        <span style={styles.pinyin}>{word.pinyin}</span>
      </p>

      <AudioRecorder onRecordingComplete={handleRecordingComplete} />
      

      <div style={styles.buttonGroup}>
        <button onClick={handleBack}>Back to Lessons</button>
        <button onClick={handleNext}>Next</button>
        <AudioPlayer filename={
    filename} />
      </div>

      {/* {!selectedLesson?.path.includes('two-character') && renderToneCheckboxes()} */}
      {/* {selectedLesson?.path.includes('two-character') && renderToneCombinationCheckboxes()} */}
    </div>
  );
};

const styles = {
  mainContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  plotContainer: {
    width: "100%",
    maxWidth: "1200px",
    marginBottom: "20px",
  },
  word: {
    fontSize: "5em",
  },
  pinyin: {
    fontSize: "3em",
  },
  audioPlayer: {
    marginBottom: "20px",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "space-between",
    width: "300px",
    marginTop: "20px",
  },
  toneCombinationContainer: {
    display: "flex",
    justifyContent: "space-between", // Align groups horizontally
            gap: "20px", // Add some space between groups
                marginTop: "20px",
  },
  toneGroup: {
    display: "flex",
    flexDirection: "column", // Stack the checkboxes vertically within each group
    alignItems: "flex-start",
  },
  checkboxLabel: {
    marginBottom: "5px", // Add some space between checkboxes
  },
  }
  ;
  export default LessonContent;
